import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Button, the desktop command control.
 *
 * Source selectors: `system.css` `.dsk-btn` + `--primary`, `--ghost`, `--sm`,
 * `--lg`, `--block`.
 *
 * Doctrine enforced (README section 8, "ink is command, accent is state"):
 * - `primary` is INK filled, near-black in light and white in dark. There is no
 *   gradient variant and no accent-filled variant in this system and none is
 *   offered here. The old `GradientButton` is being removed, not restyled.
 * - `--lg` is the commit moment: the buy box "Add to cart" and an auth submit.
 *   It is not a size knob to reach for because a button looked small.
 * - Disabled is a flat desaturated grey (`--ds-ink-disabled`), handled by the
 *   CSS. Always pass the real `disabled` attribute; never fake it with opacity.
 * - `:focus-visible` already carries the 2px ring, so nothing here fights it.
 *
 * There is NO `.dsk-btn--danger` in the vendored CSS, so no `danger` variant is
 * emitted. A destructive command is a `ghost` button whose label carries the
 * consequence, with the confirmation doing the real work.
 *
 *   <Button variant="primary" size="lg" block>Add to cart</Button>
 *   <Button as="a" href="/orders" variant="ghost">View orders</Button>
 */
export const Button = React.forwardRef(function Button(
  { variant = 'primary', size, block = false, as: Component = 'button', type, className, ...rest },
  ref,
) {
  const variantClass = { primary: 'dsk-btn--primary', ghost: 'dsk-btn--ghost' }[variant] || null;
  const sizeClass = { sm: 'dsk-btn--sm', md: null, lg: 'dsk-btn--lg' }[size] || null;

  // Only a real <button> takes a type, and it defaults to "button" so a button
  // inside a form cannot submit it by accident.
  const typeProp = Component === 'button' ? { type: type || 'button' } : {};

  return (
    <Component
      ref={ref}
      className={cn('dsk-btn', variantClass, sizeClass, block && 'dsk-btn--block', className)}
      {...typeProp}
      {...rest}
    />
  );
});
Button.displayName = 'Button';

export default Button;
